<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article>
      </main>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'
import { useUserStore } from '../stores/user-store'
import { useChatNotificationsStore } from '../stores/chat-notifications'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const GlobalStore = useGlobalStore()
const userStore = useUserStore()
const chatStore = useChatNotificationsStore()
const router = useRouter()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

// Vérifier si l'utilisateur est toujours actif
const checkUserStatus = async () => {
  const userStr = localStorage.getItem('user')
  if (!userStr) {
    return
  }

  try {
    const user = JSON.parse(userStr)
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'

    // Vérifier le statut de l'utilisateur
    const response = await fetch(`${apiBaseUrl}/users/${user.id}`, { credentials: 'include' })

    if (response.ok) {
      const currentUser = await response.json()

      // Si l'utilisateur a été désactivé, déconnecter et rediriger
      if (!currentUser.isActive) {
        localStorage.removeItem('user')
        sessionStorage.removeItem('sessionActive')
        router.push({ name: 'login' })
      }
    } else if (response.status === 404) {
      // Si l'utilisateur n'existe plus, déconnecter
      localStorage.removeItem('user')
      sessionStorage.removeItem('sessionActive')
      router.push({ name: 'login' })
    }
  } catch (error) {
    console.error('Erreur lors de la vérification du statut utilisateur:', error)
  }
}

let statusCheckInterval = null

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()

  // Vérifier le statut de l'utilisateur toutes les 30 secondes
  checkUserStatus()
  statusCheckInterval = setInterval(checkUserStatus, 30000)

  // Démarrer la synchronisation des emails et transferts
  if (userStore.userId) {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
    chatStore.startEmailSync(userStore.userId, apiBaseUrl)
    chatStore.startTransferSync(userStore.userId, apiBaseUrl)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)

  // Nettoyer l'intervalle
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval)
  }

  // Arrêter la synchronisation des emails et transferts
  chatStore.stopEmailSync()
  chatStore.stopTransferSync()
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}
</style>
